import { client } from 'clients/eyekia';
import { AutoId } from 'common/AutoId';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Primitive, Orientation, Image } from '@sophya/eyekia';
import { AssetConfigDO } from './AssetConfig';
import { EditableVec2, VEC_ZERO } from './editor/EditableVec2';
import { ImageDO, ImageIds } from './Image';
import { FramePrimitiveDO } from './Primitive';
import { AssetType, ConfigInterface, FabricInterface } from './RootStore';
import SmartSet from '../common/SmartSet';
import { uploadImageToAsset } from '../common/uploadImageToAsset';

export type OrientationKey = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

export function orientationClockwise(current: OrientationKey): OrientationKey {
  switch (current) {
    case 'N':
      return 'NE';
    case 'NE':
      return 'E';
    case 'E':
      return 'SE';
    case 'SE':
      return 'S';
    case 'S':
      return 'SW';
    case 'SW':
      return 'W';
    case 'W':
      return 'NW';
    case 'NW':
      return 'N';
    default:
      return 'N';
  }
}

export function orientationOpposite(current: OrientationKey): OrientationKey {
  switch (current) {
    case 'N':
      return 'S';
    case 'NE':
      return 'SW';
    case 'E':
      return 'W';
    case 'SE':
      return 'NW';
    case 'S':
      return 'N';
    case 'SW':
      return 'NE';
    case 'W':
      return 'E';
    case 'NW':
      return 'SE';
    default:
      return 'N';
  }
}

type PrimitiveLayout = Primitive;

export const OrientationKeys = new SmartSet<OrientationKey>([
  'N',
  'NE',
  'E',
  'SE',
  'S',
  'SW',
  'W',
  'NW',
]);

export function isOriKey(value: string): value is OrientationKey {
  return OrientationKeys.has(value as OrientationKey);
}

export class OrientationDO {
  constructor(
    public assetId: string,
    public orientation: OrientationKey,
    private fabric: FabricInterface,
    private config: ConfigInterface,
    private onSave: () => Promise<void>,
  ) {
    makeObservable(this, {
      images: observable,
      imageUploadOpen: observable,
      currentImage: observable,
      selectImage: action,
    });

    // Default go-to pivot in iso space for this orientation (furniture assets only).
    this.goToPivot = new EditableVec2(
      'Go-to pivot',
      VEC_ZERO,
      this.handleGoToPivotSubmit,
      'orange',
    );
  }

  cycleImage = () => {
    const imageKeys = Array.from(this.images.keys());
    const index = imageKeys.findIndex(key => this.currentImage === key);
    if (index === -1) {
      return;
    }
    const nextIndex = (index + 1) % imageKeys.length;
    const newImage = imageKeys[nextIndex];
    console.log({
      orientationKeys: imageKeys,
      index,
      nextIndex,
      newOrientation: newImage,
    });
    this.selectImage(newImage);
    this.selectToFabric();
  };

  currentImage: string = null;

  images: Map<string, ImageDO> = new Map();

  /**
   * Per-orientation go-to pivot in ISO coordinates.
   * Serialized as `goToPivot` on the Orientation schema.
   */
  goToPivot: EditableVec2;

  /**
   * Called when the go-to pivot is edited via the text fields.
   * Keeps the canvas dot in sync and triggers save.
   */
  private handleGoToPivotSubmit = (x: number, y: number) => {
    this.updateGoToPivotHandle();
    this.onSave();
  };

  /**
   * Sync the orange go-to pivot dot in the Fabric canvas with the current iso value.
   */
  private updateGoToPivotHandle = () => {
    const iso = this.goToPivot ? this.goToPivot.toSchema() : VEC_ZERO;
    this.fabric.setGoToPivotHandle(iso, (newIso: { x: number; y: number }) => {
      // Update the editable fields so the text values reflect the dot movement.
      if (this.goToPivot) {
        this.goToPivot.x.value = String(newIso.x);
        this.goToPivot.y.value = String(newIso.y);
      }
    });
  };

  imageUploadOpen: boolean = false;
  imageDoIds: ImageIds;

  openImageUpload = (): void => {
    let imageIds: ImageIds = {
      assetId: this.assetId,
      orientationKey: this.orientation,
      imageId: AutoId.newId(),
    };

    this.imageUploadOpen = true;
    this.imageDoIds = imageIds;
  };

  closeImageUpload = (): void => {
    this.imageUploadOpen = false;
  };

  handleImageDrop(files: File[]) {
    runInAction(() => {
      this.imageUploadOpen = false;
    });
    //console.log(files);
    if (files.length == 0) {
      console.log('no files submitted');
      return;
    }
    const f = files[0];
    const type = f.type;
    if (!(type === 'image/png')) {
      console.log('file type wrong; was -' + type + '-');
      return;
    }
    this.networkCreateImage(f);
  }

  networkCreateImage = async (img: File): Promise<void> => {
    const downloadUrl = await uploadImageToAsset(this.assetId, AssetType.Furniture, img);

    this.createImageWithUrl(downloadUrl);
  };

  private createImageWithUrl(url: string) {
    let image = new ImageDO(this.fabric, this.config, this.imageDoIds, this.onSave);
    image.setUrl(url);
    this.onSave();
    let id = this.imageDoIds.imageId;
    this.images.set(id, image);
    this.currentImage = id;
    this.fabric.deleteImage(id);
    this.fabric.loadImage(id, image.toSchema());
  }

  public createImageFromExisting(source: ImageDO, sourceConfig: AssetConfigDO) {
    let parentConfig : AssetConfigDO = this.config as AssetConfigDO;

    //We add the new frames from the source asset at the Asset parent level
    let frameIDs = new Map<string,string>();
    sourceConfig.frames.forEach((frameSource, idSource) => {
      const frameId = parentConfig.createNewFrameConfig();
      parentConfig.frames.get(frameId).updateFromSchema(frameSource.toSchema());
      frameIDs.set(idSource, frameId);
    });

    let image = new ImageDO(this.fabric, parentConfig, this.imageDoIds, this.onSave);
    image.updateFromSchema(source.toSchema());
    //We create original IDs for the primitives
    [...image.primitives.keys()].forEach(id => {
      const newId = AutoId.newId();
      let primitiveCopy = image.getPrimitive(id);
      //We replace the original frame parent for the new one that we created above
      if(primitiveCopy.type === 'frame' && frameIDs.has((primitiveCopy as FramePrimitiveDO).frameId.value)){
        (primitiveCopy as FramePrimitiveDO).frameId.value = frameIDs.get((primitiveCopy as FramePrimitiveDO).frameId.value);
      }
      image.primitives.set(newId, primitiveCopy);
      image.primitives.delete(id);
    });
    this.onSave();

    let id = AutoId.newId();
    this.images.set(id, image);
  }

  deleteImage = async (id: string) => {
    runInAction(() => {
      this.images.delete(id);
      if (id === this.currentImage) {
        this.currentImage = null;
      }
      this.fabric.deleteImage(id);
    });
    await client().v1.assetOrientationImageDelete(this.assetId, this.orientation, id);
  };

  getImage(imageId: string): ImageDO | undefined {
    return this.images.get(imageId);
  }

  selectImage(id: string) {
    this.currentImage = id;
    this.images.get(id)?.select();
  }

  selectToFabric() {
    this.fabric.selectImage(this.currentImage);
  }

  populateCanvas() {
    console.log('🎨 Populating canvas with', this.images.size, 'images for orientation', this.orientation);
    if (this.fabric) {
      this.images.forEach((image, id) => {
        console.log('🖼️ Loading image to fabric:', id, image.url._value);
        this.fabric.loadImage(id, image.toSchema());
      });
      if (this.images.size > 0) {
        this.currentImage = this.images.keys().next().value;
        console.log('🎯 Set current image to:', this.currentImage);
        // actively select the image in Fabric so it's visible and active
        this.selectToFabric();
      }
      // Create / move the go-to pivot handle (orange dot) in the canvas for this orientation.
      this.updateGoToPivotHandle();
    } else {
      console.warn('⚠️ Fabric not available for populating canvas');
    }
  }

  updateFromSchema(ori: Orientation) {
    const pivotValue = ori.goToPivot ?? VEC_ZERO;
    this.goToPivot = new EditableVec2(
      'Go-to pivot (iso)',
      pivotValue,
      this.handleGoToPivotSubmit,
      'orange',
    );

    for (const [id, data] of Object.entries(ori.images)) {
      let imageIds: ImageIds = {
        assetId: this.assetId,
        orientationKey: this.orientation,
        imageId: id,
      };
      let image = new ImageDO(this.fabric, this.config, imageIds, this.onSave);
      image.updateFromSchema(data);
      this.images.set(id, image);
    }
    this.currentImage = this.images.keys().next().value;
  }

  toSchema = (): Orientation => {
    const p: {
      [key: string]: Image;
    } = {};
    this.images.forEach((value, key) => {
      p[key] = value.toSchema();
    });

    const result: Orientation = {
      images: p,
      goToPivot: this.goToPivot ? this.goToPivot.toSchema() : null,
    };

    return result;
  };
}
